import React, { useEffect, useState } from 'react';
import '../CSS/App.css';
import { NavScroll } from './NavBar';
import SocialLinks from "./SocialLinks";
import { getEvents } from '../utils/calendarService';

export default function Schedule() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    getEvents().then(fetchedEvents => {
      const now = new Date();
      const upcomingEvents = fetchedEvents.filter(event => event.end > now);
      setEvents(upcomingEvents);
      setLoading(false);
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe && currentSlideIndex < displayedEvents.length - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    }
  };

  function formatTimeRange(start, end, isAllDay) {
    if (isAllDay) return "All Day";

    const formatTime = (date) => {
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 hour should be 12
      const minutesStr = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
      return `${hours}${minutesStr}${ampm}`;
    };

    let tz = "";
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(start);
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      tz = tzPart ? ` ${tzPart.value}` : '';
    } catch (e) {
      // Fallback
    }

    return `${formatTime(start)}–${formatTime(end)}${tz}`;
  }

  const displayedEvents = isMobile ? events : events.slice(0, 4);

  return (
    <div className='background-component'>
      <NavScroll />

      <>
        <SocialLinks />
        <main>
          <div className={`scheduleElements ${displayedEvents.length === 1 ? 'single-event-layout' : ''}`}>
            <h1 className="aboutTitle">event schedule</h1>

            {loading ? (
              <div className="scheduleLoading">Loading events...</div>
            ) : events.length === 0 ? (
              <div className="scheduleLoading">No events scheduled.</div>
            ) : isMobile ? (
              <>
                <div className="schedule-carousel-container">
                  <button 
                    className="carousel-arrow-btn prev" 
                    onClick={() => setCurrentSlideIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentSlideIndex === 0}
                  >
                    ‹
                  </button>

                  <div className="schedule-carousel-wrapper">
                    <div 
                      className="schedule-carousel-track"
                      style={{ transform: `translateX(-${currentSlideIndex * 100}%)` }}
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      {displayedEvents.map((event) => (
                        <div className="schedule-carousel-slide" key={event.id}>
                          <div className="scheduleElement">
                            <p className="scheduleDate" data-day={event.start.getDate()}>
                              {event.start.toLocaleString('en-US', { month: 'short' }).toUpperCase()}
                            </p>
                            <div className="scheduleInfo">
                              <p>{event.title}</p>
                              {event.description && <p>{event.description}</p>}
                              <p>{formatTimeRange(event.start, event.end, event.isAllDay)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button 
                    className="carousel-arrow-btn next" 
                    onClick={() => setCurrentSlideIndex(prev => Math.min(displayedEvents.length - 1, prev + 1))}
                    disabled={currentSlideIndex === displayedEvents.length - 1}
                  >
                    ›
                  </button>
                </div>

                <div className="carousel-dots">
                  {displayedEvents.map((_, idx) => (
                    <span 
                      key={idx} 
                      className={`carousel-dot ${idx === currentSlideIndex ? 'active' : ''}`}
                      onClick={() => setCurrentSlideIndex(idx)}
                    />
                  ))}
                </div>

                {events.length > 1 && (
                  <div className="schedule-toggle-container">
                    <button className="schedule-toggle-btn" onClick={() => setShowModal(true)}>
                      View Full Schedule ({events.length} Events)
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {displayedEvents.map((event) => (
                  <div className="scheduleElement" key={event.id}>
                    <p className="scheduleDate" data-day={event.start.getDate()}>
                      {event.start.toLocaleString('en-US', { month: 'short' }).toUpperCase()}
                    </p>
                    <div className="scheduleInfo">
                      <p>{event.title}</p>
                      {event.description && <p>{event.description}</p>}
                      <p>{formatTimeRange(event.start, event.end, event.isAllDay)}</p>
                    </div>
                  </div>
                ))}
                
                {events.length > 4 && (
                  <div className="schedule-toggle-container">
                    <button className="schedule-toggle-btn" onClick={() => setShowModal(true)}>
                      View Full Schedule ({events.length} Events)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </>

      {/* Full Schedule Modal */}
      {showModal && (
        <div className="schedule-modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="schedule-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="schedule-modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2 className="schedule-modal-title">Full Event Schedule</h2>
            <div className="schedule-modal-list">
              {events.map((event) => (
                <div className="schedule-modal-item" key={event.id}>
                  <div className="schedule-modal-date-badge" data-day={event.start.getDate()}>
                    <span className="schedule-modal-month">
                      {event.start.toLocaleString('en-US', { month: 'short' }).toUpperCase()}
                    </span>
                  </div>
                  <div className="schedule-modal-info">
                    <h3>{event.title}</h3>
                    {event.description && <p className="schedule-modal-desc">{event.description}</p>}
                    <span className="schedule-modal-time">
                      {formatTimeRange(event.start, event.end, event.isAllDay)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <script type="module" src="https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.esm.js"></script>
      <script nomodule src="https://unpkg.com/ionicons@5.5.2/dist/ionicons/ionicons.js"></script>
    </div>
  );
}

